import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import PropTypes from "prop-types";

const FollowCursor = ({ showSmoke = true }) => {
	const carRef = useRef(null);
	const smokeContainerRef = useRef(null);

	useEffect(() => {
		if (!carRef.current || !smokeContainerRef.current) return;

		const car = carRef.current;
		const smokeContainer = smokeContainerRef.current;

		// Cache window dimensions
		let windowWidth = window.innerWidth;
		let windowHeight = window.innerHeight;

		const position = { x: 100, y: windowHeight - 100 };
		const velocity = { x: 0, y: 0 };
		let rotation = 0;
		const target = { x: windowWidth / 2, y: windowHeight / 2 };
		let hasMouseMoved = false;
		let frameCount = 0;

		const maxSpeed = 4;
		const acceleration = 0.1;
		const friction = 0.88;
		const rotationSpeed = 0.2;
		const followDistance = 100;

		// Initialize at center
		gsap.set(car, {
			x: position.x,
			y: position.y,
			rotation: 0,
			xPercent: -50,
			yPercent: -50,
		});

		const handleResize = () => {
			windowWidth = window.innerWidth;
			windowHeight = window.innerHeight;
		};

		const createSmoke = () => {
			if (!showSmoke) return;

			const speed = Math.sqrt(
				velocity.x * velocity.x + velocity.y * velocity.y
			);

			const shouldCreate = speed > 0.5 || Math.random() > 0.7;
			if (!shouldCreate) return;

			const smoke = document.createElement("div");

			const rotRad = (rotation * Math.PI) / 180;
			const rearOffsetX = Math.cos(rotRad) * -32;
			const rearOffsetY = Math.sin(rotRad) * -32;

			const size = speed > 0.5 ? 12 : 8;
			const opacity = speed > 0.5 ? 0.4 : 0.2;

			// Batch style changes
			smoke.style.cssText = `
				position: absolute;
				left: ${position.x + rearOffsetX}px;
				top: ${position.y + rearOffsetY}px;
				width: ${size}px;
				height: ${size}px;
				background: radial-gradient(circle, rgba(120, 120, 120, ${opacity}), transparent);
				border-radius: 50%;
				pointer-events: none;
				transform: translate(-50%, -50%);
			`;

			smokeContainer.appendChild(smoke);

			gsap.to(smoke, {
				opacity: 0,
				scale: speed > 0.5 ? 2 : 1.5,
				x: rearOffsetX * 0.5,
				y: rearOffsetY * 0.5,
				duration: speed > 0.5 ? 0.8 : 1.2,
				ease: "power2.out",
				onComplete: () => smoke.remove(),
			});
		};

		const onMouseMove = (e) => {
			hasMouseMoved = true;
			target.x = e.clientX;
			target.y = e.clientY;
		};

		const ticker = () => {
			if (!hasMouseMoved) return;

			const dx = target.x - position.x;
			const dy = target.y - position.y;
			const distance = Math.sqrt(dx * dx + dy * dy);

			const targetRotation = Math.atan2(dy, dx) * (180 / Math.PI);

			let angleDiff = targetRotation - rotation;
			if (angleDiff > 180) angleDiff -= 360;
			if (angleDiff < -180) angleDiff += 360;

			rotation += angleDiff * rotationSpeed;

			if (rotation > 360) rotation -= 360;
			if (rotation < -360) rotation += 360;

			const targetDistance = followDistance;
			const distanceError = distance - targetDistance;

			if (Math.abs(distanceError) > 1) {
				const desiredVelX =
					(dx / distance) * maxSpeed * (distanceError / targetDistance);
				const desiredVelY =
					(dy / distance) * maxSpeed * (distanceError / targetDistance);

				velocity.x += (desiredVelX - velocity.x) * acceleration;
				velocity.y += (desiredVelY - velocity.y) * acceleration;
			} else {
				velocity.x *= friction;
				velocity.y *= friction;
			}

			velocity.x *= friction;
			velocity.y *= friction;

			position.x += velocity.x;
			position.y += velocity.y;

			gsap.set(car, {
				x: position.x,
				y: position.y,
				rotation: rotation,
			});

			frameCount++;
			if (frameCount % 3 === 0) {
				createSmoke();
			}
		};

		window.addEventListener("resize", handleResize);
		window.addEventListener("mousemove", onMouseMove);
		gsap.ticker.add(ticker);

		return () => {
			window.removeEventListener("resize", handleResize);
			window.removeEventListener("mousemove", onMouseMove);
			gsap.ticker.remove(ticker);
		};
	}, []);

	return (
		<>
			<div
				ref={smokeContainerRef}
				className="fixed inset-0 pointer-events-none z-40"
			/>
			<img
				ref={carRef}
				src="/icons/icons8-car-top-view-100-filled-orange.png"
				alt="Car following cursor"
				className="fixed w-8 md:w-16 h-8 md:h-16 pointer-events-none will-change-transform z-50"
				width="64"
				height="64"
			/>
		</>
	);
};

FollowCursor.propTypes = {
	showSmoke: PropTypes.bool,
};

export default FollowCursor;
